<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { v4 as uuidv4 } from "uuid";

import SpeckleLogo from "@/assets/Logos/speckle.svg";
import RadioGroup from "@/components/Reusables/Inputs/RadioGroup.vue";
import {
  CLONE_PREFERRED_MODE,
  selectionForMode,
  speckleStreamModeOptions,
  type SpeckleStreamMode,
  type SpeckleStreamSelection,
} from "./speckleStreamChoice";

// The Speckle question CloneProjectStateModal and RestoreProjectVersionModal both ask (RTLCA-5456).
// Before it existed the backend silently handed the copy the source project's Speckle project,
// leaving two projects wired to the same models with no way to ask for anything else.
//
// The question is exactly two-sided: a Speckle project of its own, or the one the project being
// copied already uses. It is NOT a browse — the API accepts any Speckle project id and matches models
// by name, but pointing the copy at some third project's models is not what cloning means, and it is
// the one path that can be rejected (no models in common, or no linked Speckle account). Keeping the
// source's own id is the API's dedicated path: no Speckle call, no token, no matching, so this option
// cannot fail for Speckle reasons — it is precisely today's behaviour, kept deliberately.
//
// TWO NOUNS SPELLED THE SAME. Speckle renamed streams to PROJECTS and branches to MODELS — `Stream`
// is deprecated in its GraphQL schema in favour of `Project` — so `speckleStreamId`, the column name
// we inherited, holds a Speckle PROJECT id. That collides with the building projects this dialog is
// copying, and the dialog's own title says "project" twice. Hence the Speckle mark and the "Speckle
// Integration" heading framing the block: inside it, "project" means a Speckle project, and the copy
// says which one by pointing at "this project" rather than naming a second noun.
//
// The two dialogs ask the same question but LEAD WITH OPPOSITE ANSWERS, via `preferredMode`: the
// preferred answer is both pre-selected and listed first. Cloning starts a new line of work, so it
// leads with a Speckle project of its own; restoring brings back a state that was this project, so it
// leads with staying on the models the project is already fed by. Both options stay available in both.
//
// Only rendered when the SOURCE project has a Speckle project: without one there is nothing to keep
// and nothing to replace, so the API ignores both fields (see offersSpeckleStreamChoice).
const props = withDefaults(
  defineProps<{
    /** The SOURCE building project's Speckle project id — what "use the same one" resolves to. */
    sourceSpeckleStreamId: string;
    /**
     * The answer this dialog leads with: pre-selected AND listed first, since a default further down
     * the list reads as a correction of the option above it. Cloning leads with a Speckle project of
     * its own, restoring with the one the project already uses — see the constants for why. Defaults
     * to the clone side, which is also the API's default.
     */
    preferredMode?: SpeckleStreamMode;
    disabled?: boolean;
  }>(),
  { preferredMode: CLONE_PREFERRED_MODE, disabled: false }
);

const emit = defineEmits<{
  "update:selection": [selection: SpeckleStreamSelection];
}>();

const modeOptions = computed(() => speckleStreamModeOptions(props.preferredMode));

// One radio group per instance: two mounted copies sharing a `name` would behave as a single group.
const groupName = `speckle-stream-mode-${uuidv4()}`;

const mode = ref<SpeckleStreamMode>(props.preferredMode);

// RadioButton reads the value off the DOM node, so it arrives as `string | number`. Narrowing here
// rather than casting keeps `mode` honest — anything unexpected falls back to `NewStream`, the answer
// that never silently hands the copy someone else's models, whichever dialog is asking.
const handleModeChange = (value: string | number) => {
  mode.value = value === "ExistingStream" ? "ExistingStream" : "NewStream";
};

const selection = computed(() => selectionForMode(mode.value, props.sourceSpeckleStreamId));

// What the chosen path means for the copy, in the words the user needs before confirming. The new
// Speckle project no longer arrives empty (RTLCA-5543): the API queues a background runner that
// copies the source's geometry into it and points the copy's models at the copied versions, so the
// consequence the label cannot carry is now the DELAY, not a permanently empty viewer.
const modeExplanation = computed(() =>
  mode.value === "ExistingStream"
    ? "The copy stays connected to the same Speckle project as this one, so the same models feed both."
    : "A new Speckle project is created for the clone and the models are copied over in the background - they appear in the 3D viewer once the copy finishes. Materials, mappings and emission factors are also copied."
);

// Immediate, so the parent holds the default choice from the first render instead of only learning
// it once the user touches something.
watch(selection, (newSelection) => emit("update:selection", newSelection), { immediate: true });
</script>

<template>
  <div class="rounded-8 p-px-12 gap-px-8 flex flex-col bg-slate-100 ring-1 ring-slate-200">
    <div class="gap-px-8 flex items-center">
      <img
        :src="SpeckleLogo"
        class="icon-sm shrink-0"
        alt=""
        aria-hidden="true" />
      <p class="t-small-bold text-type-default">Speckle Integration</p>
    </div>

    <!-- aria-label falls through to the role="radiogroup" element, naming the group for a screen
         reader without repeating the heading above as a second visible label. It says "Speckle
         project" where the heading says "Speckle Integration": read on its own, out of the block, the
         options are two bare "project"s and need the noun. -->
    <RadioGroup
      aria-label="Speckle project"
      :model-value="mode"
      :name="groupName"
      :options="modeOptions"
      :disabled="disabled"
      @update:model-value="handleModeChange" />

    <p class="t-caption text-type-muted">{{ modeExplanation }}</p>
  </div>
</template>
